import React, { useMemo, useState, useCallback } from "react";
import ContentModal from "components/ContentModal";
import { ActionButton } from "components/ActionButton";
import {
  Box,
  FormControl,
  FormControlLabel,
  FormLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import useFetchHPClients from "hooks/useFetchHPClients";
import moment from "moment";
import { useCreateReleaseSchedule } from "hooks/useReleaseSchedule";
import { useDispatch } from "react-redux";
import AddPoliciesPopup from "../AddPoliciesPopup";
import api from "api";
import { setDialog } from "slices/appSlice";
import { formErrorStrfromObj } from "utils/shared";

const textFieldStyle = { width: "30%" };
const descriptionStyle = { width: "60%" };

function CreateRelease({ open, handleClose, reloadParent }) {
  const [hpClients] = useFetchHPClients();
  const [selectedHP, setselectedHP] = useState("");
  const dispatch = useDispatch();

  const [releaseNumber, setReleaseNumber] = useState("");
  const [targetUAT, setTargetUAT] = useState(null);
  const [targetProd, setTargetProd] = useState(null);
  const [description, setDescription] = useState("");
  const [releaseType, setReleaseType] = useState("");

  const [createReleaseSchedule] = useCreateReleaseSchedule();
  const [showAddPolicies, setShowAddPolicies] = useState(false);
  const [releaseKey, setReleaseKey] = useState(null);
  const [availablePolicies, setAvailablePolicies] = useState([]);

  // removed the two useEffects that called setReleaseNumber

  const closeCreateRelease = () => {
    reset();
    reloadParent();
  };

  const handleHPChange = useCallback(
    (e) => {
      const hp = hpClients.find(
        (client) => client.healthPlanKey === e.target.value
      );
      setselectedHP(hp || "");
      setReleaseNumber(hp ? hp.healthPlanName + "_" : "");
    },
    [hpClients]
  );

  const reset = useCallback(() => {
    handleClose();
    setselectedHP("");
    setReleaseNumber("");
    setTargetUAT(null);
    setTargetProd(null);
    setDescription("");
    setReleaseType("");
    setShowAddPolicies(false);
    setReleaseKey(null);
    setAvailablePolicies([]);
  }, [handleClose]);

  const handleSubmit = useCallback(async () => {
    const fetchAvailablePolicies = async (key) => {
      try {
        const policyResponse =
          await api.ReleaseSchedule.getPolicyMappingDetails(key);
        setAvailablePolicies(policyResponse.data);
      } catch (error) {
        dispatch(
          setDialog({ title: "Error", message: formErrorStrfromObj(error) })
        );
      }
    };

    try {
      const response = await createReleaseSchedule({
        healthPlanKey: selectedHP.healthPlanKey,
        releaseDescriptionText: description,
        targetUatDate: targetUAT
          ? moment(targetUAT).format("YYYY-MM-DD")
          : null,
        targetProdDate: targetProd
          ? moment(targetProd).format("YYYY-MM-DD")
          : null,
        releaseNumber,
        releaseTypeText: releaseType,
      });
      const releaseKey = response?.data?.releaseScheduleKey;
      if (releaseKey) {
        setReleaseKey(releaseKey);
        fetchAvailablePolicies(releaseKey);
        setShowAddPolicies(true);
      }
    } catch (error) {
      dispatch(
        setDialog({ title: "Error", message: formErrorStrfromObj(error) })
      );
    }
  }, [
    createReleaseSchedule,
    selectedHP,
    description,
    targetUAT,
    targetProd,
    releaseNumber,
    releaseType,
    dispatch,
  ]);

  const Content = useMemo(
    () => (
      <Box>
        <Stack direction="column" spacing={2} paddingTop={2}>
          <Box>
            <FormControl sx={descriptionStyle}>
              <InputLabel id="release-hp-select">Select HealthPlan</InputLabel>
              <Select
                margin="dense"
                id="release-hp-select"
                label="Select HealthPlan"
                value={selectedHP?.healthPlanKey || ""}
                onChange={handleHPChange}
              >
                {hpClients.map((client) => (
                  <MenuItem
                    key={client.healthPlanKey}
                    value={client.healthPlanKey}
                  >
                    {client.healthPlanDisplayName} ({client.healthPlanName})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <TextField
            label="Release Number"
            sx={textFieldStyle}
            value={releaseNumber}
            onChange={(e) => setReleaseNumber(e.target.value)}
          />
          <TextField
            label="Release Description"
            multiline
            rows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            sx={descriptionStyle}
          />
          <FormControl>
            <FormLabel id="release-type-radio">Release Type</FormLabel>
            <RadioGroup
              value={releaseType}
              onChange={(e) => setReleaseType(e.target.value)}
              aria-labelledby="release-type-radio"
            >
              <Stack direction={"row"} spacing={2}>
                <FormControlLabel
                  value="adHoc"
                  control={<Radio />}
                  label="Ad Hoc"
                />
                <FormControlLabel
                  value="planned"
                  control={<Radio />}
                  label="Planned"
                />
              </Stack>
            </RadioGroup>
          </FormControl>
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <DatePicker
              value={targetUAT}
              onChange={(newValue) => setTargetUAT(newValue)}
              label="Target UAT"
              sx={textFieldStyle}
            />
            <DatePicker
              value={targetProd}
              onChange={(newValue) => setTargetProd(newValue)}
              label="Target Prod"
              sx={textFieldStyle}
            />
          </LocalizationProvider>
        </Stack>
      </Box>
    ),
    [
      selectedHP?.healthPlanKey,
      handleHPChange,
      hpClients,
      releaseNumber,
      description,
      releaseType,
      targetUAT,
      targetProd,
    ]
  );

  const disabled = !(selectedHP && releaseNumber && releaseType);

  return (
    <>
      <ContentModal
        title={
          <Typography sx={{ fontSize: "1.5rem" }}>Create Release</Typography>
        }
        open={open}
        handleClose={reset}
        maxWidth="md"
        content={Content}
        actions={
          <ActionButton onClick={handleSubmit} disabled={disabled}>
            Continue
          </ActionButton>
        }
      />
      {showAddPolicies && (
        <AddPoliciesPopup
          open={showAddPolicies}
          handleClose={() => {
            setShowAddPolicies(false);
            closeCreateRelease();
          }}
          reloadParent={closeCreateRelease}
          releaseKey={releaseKey}
          releaseNumber={releaseNumber}
          mappedPolicies={null}
          availablePolicies={availablePolicies}
          canUpdatePolicies={true}
        />
      )}
    </>
  );
}

export default CreateRelease;
